/**
 * ServiceVisuals — a library of pure SVG/CSS animated visualizations used
 * across the three service detail pages to break up text and tell the
 * operating-discipline story visually.
 *
 * Owner directive: "10–15 SVG/CSS animated visualizations". This file
 * provides exactly 15 named components covering:
 *
 *   ETC / mobility               (5):  EtcGateFlow, TollMeisaiPulse,
 *                                       PlateScanCascade, NetworkPing,
 *                                       FleetGridHeartbeat
 *   Data / records / receipts    (5):  ReceiptStream, DataPipeline,
 *                                       OrbitingNodes, RadarSweep,
 *                                       LedgerWaveform
 *   Trust / customer support     (5):  ShieldPulse, HumanInTheLoopRing,
 *                                       MultilingualOrbit, AccuracyGauge,
 *                                       CountUpBadge
 *
 * Design rules followed for every viz:
 *   • Pure SVG + CSS (no JS animations, no canvas).
 *   • Color via prop `accent` (defaults to service accent on the page).
 *   • `prefers-reduced-motion` honored — animations pause when the user
 *     opts out.
 *   • All `data-testid`s present so the testing agent can target each viz.
 *   • Sizing controlled by the parent container (every viz is `w-full
 *     h-full` SVG with `viewBox`). Wrap in a div with explicit height.
 *
 * Usage:
 *   import { EtcGateFlow } from "@/components/ServiceVisuals";
 *   <div className="h-40 rounded-2xl bg-[#F4EEF9] p-4">
 *     <EtcGateFlow accent="#5B2E83" />
 *   </div>
 */

import { useId } from "react";

/* ------------------------------------------------------------------ */
/* Shared CSS — injected once via a single <style> tag in <ServiceVisualStyles/>.
 * We avoid Tailwind here because we need real CSS keyframes.            */
/* ------------------------------------------------------------------ */

export function ServiceVisualStyles() {
  return (
    <style>{`
      @media (prefers-reduced-motion: reduce) {
        .svc-anim, .svc-anim * { animation: none !important; }
      }
      @keyframes svc-flow-x {
        0%   { transform: translateX(-110%); opacity: 0; }
        15%  { opacity: 1; }
        85%  { opacity: 1; }
        100% { transform: translateX(110%); opacity: 0; }
      }
      @keyframes svc-pulse-r {
        0%   { r: var(--r0, 4); opacity: 0.85; }
        70%  { r: var(--r1, 14); opacity: 0; }
        100% { r: var(--r1, 14); opacity: 0; }
      }
      @keyframes svc-dash-march {
        to { stroke-dashoffset: -32; }
      }
      @keyframes svc-fade-in-out {
        0%, 100% { opacity: 0; }
        15%, 85% { opacity: 1; }
      }
      @keyframes svc-spin-slow {
        to { transform: rotate(360deg); }
      }
      @keyframes svc-orbit {
        to { transform: rotate(360deg); }
      }
      @keyframes svc-bar-grow {
        0%   { transform: scaleY(0.2); }
        50%  { transform: scaleY(1); }
        100% { transform: scaleY(0.2); }
      }
      @keyframes svc-count-blink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.55; }
      }
      @keyframes svc-radar-sweep {
        to { transform: rotate(360deg); }
      }
      @keyframes svc-wave {
        0%   { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      @keyframes svc-shield-pulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.04); opacity: 0.92; }
      }
    `}</style>
  );
}

/* ===================================================================== */
/* 1. EtcGateFlow — cars passing through an ETC lane, plate-scan ping     */
/* ===================================================================== */
export function EtcGateFlow({ accent = "#5B2E83" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 240 120" className="svc-anim w-full h-full" data-testid="viz-etc-gate-flow">
      {/* gantry */}
      <rect x="100" y="20" width="40" height="6" rx="2" fill={accent} opacity="0.85" />
      <rect x="104" y="26" width="4" height="50" fill={accent} opacity="0.85" />
      <rect x="132" y="26" width="4" height="50" fill={accent} opacity="0.85" />
      {/* "ETC" panel */}
      <rect x="108" y="32" width="24" height="14" rx="2" fill="white" stroke={accent} strokeWidth="1.5" />
      <text x="120" y="42" textAnchor="middle" fontSize="8" fontWeight="700" fill={accent} fontFamily="ui-monospace, monospace">ETC</text>
      {/* road */}
      <rect x="0" y="80" width="240" height="20" fill="#F1F5F9" />
      <line x1="0" y1="90" x2="240" y2="90" stroke={accent} strokeWidth="1" strokeDasharray="8 8"
            style={{ animation: "svc-dash-march 1.4s linear infinite" }} />
      {/* car */}
      <g style={{ animation: "svc-flow-x 4s linear infinite" }}>
        <rect x="0" y="74" width="28" height="14" rx="3" fill={accent} />
        <rect x="4" y="68" width="20" height="8" rx="2" fill={accent} opacity="0.7" />
        <circle cx="8" cy="89" r="3" fill="#0F1D2D" />
        <circle cx="22" cy="89" r="3" fill="#0F1D2D" />
      </g>
      {/* ping under gantry */}
      <circle cx="120" cy="74" r="0" fill={accent} opacity="0"
              style={{ animation: "svc-pulse-r 4s ease-out infinite", animationDelay: "1.5s", ["--r1"]: "18" }} />
      <circle cx="120" cy="74" r="3" fill={accent}
              style={{ animation: "svc-fade-in-out 4s ease-out infinite", animationDelay: "1.6s" }} />
    </svg>
  );
}

/* ===================================================================== */
/* 2. TollMeisaiPulse — pulsing nodes for "MEISAI sync running"           */
/* ===================================================================== */
export function TollMeisaiPulse({ accent = "#2F8F3C" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 240 120" className="svc-anim w-full h-full" data-testid="viz-toll-meisai-pulse">
      <line x1="20" y1="60" x2="220" y2="60" stroke={accent} strokeWidth="1.5" strokeDasharray="3 5" opacity="0.5" />
      {[20, 70, 120, 170, 220].map((cx, i) => (
        <g key={cx}>
          <circle cx={cx} cy="60" r="14" fill={accent} opacity="0.18"
                  style={{ animation: "svc-pulse-r 2.4s ease-out infinite", animationDelay: `${i * 0.4}s`, ["--r0"]: "8", ["--r1"]: "22" }} />
          <circle cx={cx} cy="60" r="5" fill={accent} />
          <text x={cx} y="92" textAnchor="middle" fontSize="7" fontWeight="700" fill={accent} fontFamily="ui-monospace, monospace">
            {["00:00", "06:00", "12:00", "18:00", "23:59"][i]}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* ===================================================================== */
/* 3. PlateScanCascade — plate frames cascading down a scanner            */
/* ===================================================================== */
export function PlateScanCascade({ accent = "#0E5FB0" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 240 140" className="svc-anim w-full h-full" data-testid="viz-plate-scan-cascade">
      <rect x="20" y="10" width="200" height="2" fill={accent} opacity="0.4" />
      <rect x="20" y="128" width="200" height="2" fill={accent} opacity="0.4" />
      {/* scanner laser */}
      <line x1="20" y1="20" x2="220" y2="20" stroke={accent} strokeWidth="0.5" opacity="0.5"
            style={{ animation: "svc-flow-x 2.6s ease-in-out infinite" }} />
      {[0, 1, 2, 3].map((i) => (
        <g key={i} transform={`translate(60, ${30 + i * 24})`} opacity={1 - i * 0.18}>
          <rect width="120" height="18" rx="2" fill="white" stroke={accent} strokeWidth="1" />
          <rect x="6" y="3" width="14" height="12" rx="1" fill={accent} opacity="0.85" />
          <text x="26" y="13" fontSize="9" fontWeight="800" fill="#0F1D2D" fontFamily="ui-monospace, monospace">
            {["品川 500 あ 12-34", "練馬 300 い 56-78", "横浜 580 う 90-12", "湘南 330 え 34-56"][i]}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* ===================================================================== */
/* 4. NetworkPing — geographic pings across Japan-ish silhouette          */
/* ===================================================================== */
export function NetworkPing({ accent = "#5B2E83" }) {
  const cities = [
    { cx: 70, cy: 70, label: "札幌" },
    { cx: 105, cy: 60, label: "仙台" },
    { cx: 130, cy: 75, label: "東京" },
    { cx: 138, cy: 78, label: "横須賀" },
    { cx: 155, cy: 85, label: "名古屋" },
    { cx: 175, cy: 90, label: "大阪" },
    { cx: 195, cy: 90, label: "福岡" },
    { cx: 105, cy: 105, label: "沖縄" },
  ];
  return (
    <svg aria-hidden="true" viewBox="0 0 240 130" className="svc-anim w-full h-full" data-testid="viz-network-ping">
      {/* faint horizontal bands suggesting Japan arc */}
      <path d="M 50 40 Q 130 90 220 100" stroke={accent} strokeWidth="1" fill="none" opacity="0.18" />
      {cities.map((c, i) => (
        <g key={c.label}>
          <circle cx={c.cx} cy={c.cy} r="3" fill={accent} />
          <circle cx={c.cx} cy={c.cy} r="3" fill={accent} opacity="0.4"
                  style={{ animation: "svc-pulse-r 2.6s ease-out infinite", animationDelay: `${i * 0.28}s`, ["--r0"]: "3", ["--r1"]: "16" }} />
        </g>
      ))}
      <text x="120" y="125" textAnchor="middle" fontSize="7" fontWeight="700" fill={accent} fontFamily="ui-monospace, monospace" letterSpacing="2">
        ACROSS JAPAN
      </text>
    </svg>
  );
}

/* ===================================================================== */
/* 5. FleetGridHeartbeat — grid of active fleet cells, breathing          */
/* ===================================================================== */
export function FleetGridHeartbeat({ accent = "#0E5FB0" }) {
  const cells = Array.from({ length: 32 }, (_, i) => i);
  return (
    <svg aria-hidden="true" viewBox="0 0 240 120" className="svc-anim w-full h-full" data-testid="viz-fleet-grid-heartbeat">
      {cells.map((i) => {
        const col = i % 8;
        const row = Math.floor(i / 8);
        return (
          <rect
            key={i}
            x={20 + col * 25}
            y={20 + row * 22}
            width="20"
            height="17"
            rx="2"
            fill={accent}
            opacity={0.55}
            style={{
              transformOrigin: "center",
              animation: "svc-bar-grow 2.4s ease-in-out infinite",
              animationDelay: `${(i % 12) * 0.13}s`,
            }}
          />
        );
      })}
    </svg>
  );
}

/* ===================================================================== */
/* 6. ReceiptStream — receipts streaming from left to right               */
/* ===================================================================== */
export function ReceiptStream({ accent = "#2F8F3C" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 240 120" className="svc-anim w-full h-full" data-testid="viz-receipt-stream">
      <line x1="0" y1="60" x2="240" y2="60" stroke={accent} strokeWidth="1" strokeDasharray="4 4" opacity="0.4" />
      {[0, 1, 2, 3].map((i) => (
        <g key={i} style={{ animation: "svc-flow-x 5s linear infinite", animationDelay: `${i * 1.25}s` }}>
          <rect x="0" y="42" width="36" height="36" rx="2" fill="white" stroke={accent} strokeWidth="1.5" />
          <line x1="4" y1="50" x2="32" y2="50" stroke={accent} strokeWidth="1" opacity="0.7" />
          <line x1="4" y1="55" x2="28" y2="55" stroke={accent} strokeWidth="1" opacity="0.6" />
          <line x1="4" y1="60" x2="32" y2="60" stroke={accent} strokeWidth="1" opacity="0.55" />
          <line x1="4" y1="65" x2="24" y2="65" stroke={accent} strokeWidth="1" opacity="0.45" />
          <text x="18" y="74" textAnchor="middle" fontSize="6" fontWeight="800" fill={accent} fontFamily="ui-monospace, monospace">¥</text>
        </g>
      ))}
    </svg>
  );
}

/* ===================================================================== */
/* 7. DataPipeline — Input → Extract → Validate → Export pipeline         */
/* ===================================================================== */
export function DataPipeline({ accent = "#0E5FB0" }) {
  const stages = ["Input", "Extract", "Validate", "Export"];
  return (
    <svg aria-hidden="true" viewBox="0 0 240 80" className="svc-anim w-full h-full" data-testid="viz-data-pipeline">
      {stages.map((s, i) => (
        <g key={s} transform={`translate(${20 + i * 55}, 28)`}>
          <rect width="42" height="28" rx="4" fill={accent} opacity="0.18" />
          <rect width="42" height="28" rx="4" fill="none" stroke={accent} strokeWidth="1.2" />
          <text x="21" y="18" textAnchor="middle" fontSize="8" fontWeight="700" fill={accent} fontFamily="ui-monospace, monospace">{s}</text>
        </g>
      ))}
      {/* arrows between stages */}
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <line
            x1={62 + i * 55}
            y1={42}
            x2={75 + i * 55}
            y2={42}
            stroke={accent}
            strokeWidth="1.5"
            strokeDasharray="3 3"
            style={{ animation: "svc-dash-march 1s linear infinite", animationDelay: `${i * 0.18}s` }}
          />
          <polygon
            points={`${75 + i * 55},39 ${75 + i * 55},45 ${79 + i * 55},42`}
            fill={accent}
          />
        </g>
      ))}
    </svg>
  );
}

/* ===================================================================== */
/* 8. OrbitingNodes — 5 nodes orbiting a center (AI team metaphor)        */
/* ===================================================================== */
export function OrbitingNodes({ accent = "#5B2E83" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 240 160" className="svc-anim w-full h-full" data-testid="viz-orbiting-nodes">
      <g transform="translate(120 80)">
        <circle r="18" fill={accent} opacity="0.18" />
        <circle r="11" fill={accent} />
        <text y="3" textAnchor="middle" fontSize="9" fontWeight="800" fill="white" fontFamily="ui-monospace, monospace">AI</text>
        {/* orbit rings */}
        <circle r="56" fill="none" stroke={accent} strokeWidth="0.6" strokeDasharray="2 4" opacity="0.5" />
        <g style={{ animation: "svc-orbit 24s linear infinite", transformOrigin: "0 0" }}>
          {[0, 72, 144, 216, 288].map((deg, i) => (
            <g key={deg} transform={`rotate(${deg}) translate(56 0)`}>
              <circle r="6" fill={accent} />
              <text textAnchor="middle" y="2" fontSize="6" fontWeight="800" fill="white" fontFamily="ui-monospace, monospace">
                {["S", "R", "Y", "H", "A"][i]}
              </text>
            </g>
          ))}
        </g>
      </g>
    </svg>
  );
}

/* ===================================================================== */
/* 9. RadarSweep — circular radar sweep for "monitored 24/7"              */
/* ===================================================================== */
export function RadarSweep({ accent = "#5B2E83" }) {
  const gid = useId();
  return (
    <svg aria-hidden="true" viewBox="0 0 160 160" className="svc-anim w-full h-full" data-testid="viz-radar-sweep">
      <defs>
        <linearGradient id={`radar-${gid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={accent} stopOpacity="0.4" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <circle cx="80" cy="80" r="74" fill="none" stroke={accent} strokeWidth="0.6" opacity="0.4" />
      <circle cx="80" cy="80" r="54" fill="none" stroke={accent} strokeWidth="0.6" opacity="0.3" />
      <circle cx="80" cy="80" r="34" fill="none" stroke={accent} strokeWidth="0.6" opacity="0.3" />
      <circle cx="80" cy="80" r="14" fill="none" stroke={accent} strokeWidth="0.6" opacity="0.3" />
      <line x1="80" y1="80" x2="80" y2="6" stroke={accent} strokeWidth="0.6" opacity="0.25" />
      <line x1="80" y1="80" x2="154" y2="80" stroke={accent} strokeWidth="0.6" opacity="0.25" />
      {/* sweep */}
      <g style={{ transformOrigin: "80px 80px", animation: "svc-radar-sweep 4s linear infinite" }}>
        <path d="M 80 80 L 154 80 A 74 74 0 0 0 80 6 Z" fill={`url(#radar-${gid})`} />
      </g>
      {/* blips */}
      <circle cx="115" cy="42" r="2.5" fill={accent} style={{ animation: "svc-count-blink 1.4s ease-in-out infinite" }} />
      <circle cx="48" cy="100" r="2.5" fill={accent} style={{ animation: "svc-count-blink 1.4s ease-in-out infinite", animationDelay: "0.5s" }} />
      <circle cx="120" cy="120" r="2.5" fill={accent} style={{ animation: "svc-count-blink 1.4s ease-in-out infinite", animationDelay: "0.9s" }} />
    </svg>
  );
}

/* ===================================================================== */
/* 10. LedgerWaveform — equalizer-style bars for "records flowing"        */
/* ===================================================================== */
export function LedgerWaveform({ accent = "#2F8F3C" }) {
  const bars = Array.from({ length: 24 }, (_, i) => i);
  return (
    <svg aria-hidden="true" viewBox="0 0 240 80" className="svc-anim w-full h-full" data-testid="viz-ledger-waveform">
      {bars.map((i) => (
        <rect
          key={i}
          x={10 + i * 9.5}
          y="30"
          width="5"
          height="40"
          rx="1"
          fill={accent}
          style={{
            transformOrigin: "center",
            animation: "svc-bar-grow 1.8s ease-in-out infinite",
            animationDelay: `${i * 0.07}s`,
          }}
        />
      ))}
      <line x1="0" y1="74" x2="240" y2="74" stroke={accent} strokeWidth="1" opacity="0.4" />
    </svg>
  );
}

/* ===================================================================== */
/* 11. ShieldPulse — secured / KYC shield with pulse                      */
/* ===================================================================== */
export function ShieldPulse({ accent = "#0F1D2D" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 160 160" className="svc-anim w-full h-full" data-testid="viz-shield-pulse">
      <g style={{ transformOrigin: "80px 80px", animation: "svc-shield-pulse 2.6s ease-in-out infinite" }}>
        <path
          d="M 80 18 L 130 38 L 130 88 Q 130 124 80 142 Q 30 124 30 88 L 30 38 Z"
          fill={accent}
          opacity="0.95"
        />
        <path
          d="M 60 84 L 76 100 L 104 70"
          stroke="white"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>
      <circle cx="80" cy="80" r="78" fill="none" stroke={accent} strokeWidth="0.5" opacity="0.3"
              style={{ animation: "svc-pulse-r 2.6s ease-out infinite", ["--r0"]: "70", ["--r1"]: "84" }} />
    </svg>
  );
}

/* ===================================================================== */
/* 12. HumanInTheLoopRing — robot icon → human review icon → out          */
/* ===================================================================== */
export function HumanInTheLoopRing({ accent = "#5B2E83" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 240 120" className="svc-anim w-full h-full" data-testid="viz-human-in-the-loop-ring">
      {/* AI */}
      <g transform="translate(40 60)">
        <rect x="-18" y="-18" width="36" height="36" rx="6" fill={accent} opacity="0.95" />
        <text textAnchor="middle" y="6" fontSize="13" fontWeight="800" fill="white" fontFamily="ui-monospace, monospace">AI</text>
        <text x="0" y="34" textAnchor="middle" fontSize="7" fill={accent} fontWeight="700" fontFamily="ui-monospace, monospace">DRAFT</text>
      </g>
      {/* arrow */}
      <line x1="62" y1="60" x2="98" y2="60" stroke={accent} strokeWidth="1.6" strokeDasharray="3 3"
            style={{ animation: "svc-dash-march 1s linear infinite" }} />
      <polygon points="98,57 98,63 103,60" fill={accent} />
      {/* Human review */}
      <g transform="translate(120 60)">
        <circle r="22" fill="white" stroke={accent} strokeWidth="1.8" />
        <circle cy="-6" r="6" fill={accent} />
        <path d="M -10 12 Q 0 0 10 12 L 10 16 Q 0 22 -10 16 Z" fill={accent} />
        <text x="0" y="42" textAnchor="middle" fontSize="7" fill={accent} fontWeight="700" fontFamily="ui-monospace, monospace">HUMAN</text>
      </g>
      {/* arrow */}
      <line x1="146" y1="60" x2="182" y2="60" stroke={accent} strokeWidth="1.6" strokeDasharray="3 3"
            style={{ animation: "svc-dash-march 1s linear infinite", animationDelay: "0.4s" }} />
      <polygon points="182,57 182,63 187,60" fill={accent} />
      {/* Approved */}
      <g transform="translate(208 60)">
        <rect x="-18" y="-18" width="36" height="36" rx="6" fill="#2F8F3C" />
        <path d="M -8 0 L -2 6 L 8 -6" stroke="white" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <text x="0" y="34" textAnchor="middle" fontSize="7" fill="#2F8F3C" fontWeight="700" fontFamily="ui-monospace, monospace">SENT</text>
      </g>
    </svg>
  );
}

/* ===================================================================== */
/* 13. MultilingualOrbit — EN / JA orbiting badge                         */
/* ===================================================================== */
export function MultilingualOrbit({ accent = "#0E5FB0" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 160 160" className="svc-anim w-full h-full" data-testid="viz-multilingual-orbit">
      <g transform="translate(80 80)">
        <circle r="22" fill={accent} />
        <text textAnchor="middle" y="-2" fontSize="9" fontWeight="800" fill="white" fontFamily="ui-monospace, monospace">JA</text>
        <text textAnchor="middle" y="9" fontSize="9" fontWeight="800" fill="white" fontFamily="ui-monospace, monospace">EN</text>
        <circle r="60" fill="none" stroke={accent} strokeWidth="0.8" strokeDasharray="3 5" opacity="0.5" />
        <g style={{ animation: "svc-orbit 18s linear infinite", transformOrigin: "0 0" }}>
          {[0, 90, 180, 270].map((deg, i) => (
            <g key={deg} transform={`rotate(${deg}) translate(60 0)`}>
              <circle r="10" fill="white" stroke={accent} strokeWidth="1.6" />
              <text textAnchor="middle" y="3" fontSize="8" fontWeight="800" fill={accent} fontFamily="ui-monospace, monospace">
                {["JA", "EN", "JA", "EN"][i]}
              </text>
            </g>
          ))}
        </g>
      </g>
    </svg>
  );
}

/* ===================================================================== */
/* 14. AccuracyGauge — circular gauge animating to 99%                    */
/* ===================================================================== */
export function AccuracyGauge({ accent = "#2F8F3C", value = 99.97 }) {
  const r = 60;
  const circumference = 2 * Math.PI * r;
  const dash = circumference * (value / 100);
  return (
    <svg aria-hidden="true" viewBox="0 0 160 160" className="svc-anim w-full h-full" data-testid="viz-accuracy-gauge">
      <circle cx="80" cy="80" r={r} fill="none" stroke="#E2E8F0" strokeWidth="10" />
      <circle
        cx="80"
        cy="80"
        r={r}
        fill="none"
        stroke={accent}
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`}
        transform="rotate(-90 80 80)"
        style={{
          transition: "stroke-dasharray 1.6s ease-out",
        }}
      />
      <text x="80" y="78" textAnchor="middle" fontSize="22" fontWeight="800" fill="#0F1D2D" fontFamily="ui-monospace, monospace">
        {value.toFixed(2)}%
      </text>
      <text x="80" y="96" textAnchor="middle" fontSize="8" letterSpacing="2" fontWeight="700" fill={accent} fontFamily="ui-monospace, monospace">
        ACCURACY
      </text>
    </svg>
  );
}

/* ===================================================================== */
/* 15. CountUpBadge — blinking count-up live indicator                    */
/* ===================================================================== */
export function CountUpBadge({ accent = "#0E5FB0", labelEn = "ACTIVE", labelJa = "稼働中" }) {
  // The pill sits on a dark navy fill (#0F1D2D). Some service accents
  // (purple #5B2E83 for JEC, deep blue #0E5FB0 for JTR) are too close
  // in luminance to the navy background, so the "24 / 7" caption used
  // to disappear. We render it in a light, brand-safe sky tint that
  // hits AA contrast on the navy regardless of which accent the parent
  // passed in. The blinking dot (which carries the brand colour cue)
  // still uses the raw accent.
  const captionColor = "#7AB8E8";
  return (
    <svg aria-hidden="true" viewBox="0 0 240 80" className="svc-anim w-full h-full" data-testid="viz-count-up-badge">
      <rect x="20" y="22" width="200" height="36" rx="18" fill="#0F1D2D" />
      <circle cx="40" cy="40" r="6" fill={accent}
              style={{ animation: "svc-count-blink 1.4s ease-in-out infinite" }} />
      <text x="58" y="44" fontSize="11" fontWeight="800" fill="white" fontFamily="ui-monospace, monospace" letterSpacing="2">
        {labelEn} · {labelJa}
      </text>
      <text x="220" y="44" textAnchor="end" fontSize="9.5" fontWeight="800" fill={captionColor} fontFamily="ui-monospace, monospace" letterSpacing="2">
        24 / 7
      </text>
    </svg>
  );
}
